// ═══════════════════════════════════════════════════════════════
// BLACKBOARD — Verify Certificate (PUBLIC page, no auth)
// Looks up a certificate by code and displays its status
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Text,
  Stack,
  Center,
  Divider,
  Group,
  Loader,
  ThemeIcon,
} from '../../components/gab/GabComponents';
import {
  IconCircleCheck,
  IconCircleX,
  IconShieldCheck,
} from '@tabler/icons-react';
import { fetchTable, TABLES } from '../../lib/api-switch';
import { FIELDS } from '../../api/fields';
import dayjs from 'dayjs';

// ── Types ────────────────────────────────────────────────────

interface CertificateData {
  id: number;
  userId: string;
  userName: string;
  trainingName: string;
  trainingId: string;
  certCode: string;
  completedDate: string;
  expiresDate: string;
  datecreated: string;
}

// ── Component ────────────────────────────────────────────────

export default function VerifyCertificate() {
  const { certCode } = useParams<{ certCode: string }>();
  const [loading, setLoading] = useState(true);
  const [certificate, setCertificate] = useState<CertificateData | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    async function fetchCert() {
      if (!certCode) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        // Fetch all certificates and filter client-side by certCode
        const FC = FIELDS.bbCertificates;
        const data = await fetchTable<Record<string, unknown>>(TABLES.bbCertificates, { anonymous: true });
        const match = data.find(
          row => (String(row[FC.certCode] ?? '')).trim().toLowerCase() === certCode.trim().toLowerCase(),
        );

        if (!match) {
          setNotFound(true);
        } else {
          setCertificate({
            id: Number(match.record_id) || 0,
            userId: String(match[FC.userId] ?? ''),
            userName: String(match[FC.userName] ?? ''),
            trainingName: String(match[FC.trainingName] ?? ''),
            trainingId: String(match[FC.trainingId] ?? ''),
            certCode: String(match[FC.certCode] ?? ''),
            completedDate: String(match[FC.completedDate] ?? ''),
            expiresDate: String(match[FC.expiresDate] ?? ''),
            datecreated: String(match.created_at ?? ''),
          });
        }
      } catch (err) {
        console.error('Certificate verification error:', err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    }

    fetchCert();
  }, [certCode]);

  const completedDate = certificate?.completedDate || null;
  const expiresDate = certificate?.expiresDate || null;
  const isExpired = expiresDate ? dayjs(expiresDate).isBefore(dayjs()) : false;

  return (
    <Box
      mih="100vh"
      style={{
        background: 'linear-gradient(135deg, #f0f4f8 0%, #d9e2ec 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <Paper p="xl" shadow="md" radius="md" withBorder maw={500} w="100%">
        {/* Logo / Shield */}
        <Center mb="md">
          <ThemeIcon size={64} radius="xl" variant="light" color="indigo">
            <IconShieldCheck size={36} />
          </ThemeIcon>
        </Center>

        <Text ta="center" fw={700} size="lg" c="#1a365d" mb="lg">
          Certificate Verification
        </Text>

        <Divider mb="lg" />

        {/* Loading */}
        {loading && (
          <Center py="xl">
            <Stack align="center" gap="md">
              <Loader size="md" color="blue" />
              <Text size="sm" c="dimmed">
                Verifying certificate...
              </Text>
            </Stack>
          </Center>
        )}

        {/* Found */}
        {!loading && certificate && (
          <Stack align="center" gap="md">
            {isExpired ? (
              <IconCircleX size={56} color="#dc2626" />
            ) : (
              <IconCircleCheck size={56} color="#16a34a" />
            )}

            <Text fw={700} size="xl" c={isExpired ? '#dc2626' : '#16a34a'}>
              {isExpired ? 'Certificate Expired' : 'Certificate Verified'}
            </Text>

            <Paper
              p="md"
              w="100%"
              bg="gray.0"
              radius="md"
              style={{ border: '1px solid var(--mantine-color-gray-3)' }}
            >
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Student Name
                  </Text>
                  <Text size="sm" fw={600}>
                    {certificate.userName || '\u2014'}
                  </Text>
                </Group>
                <Divider />
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Training / Course
                  </Text>
                  <Text size="sm" fw={600} ta="right" maw={220}>
                    {certificate.trainingName || '\u2014'}
                  </Text>
                </Group>
                <Divider />
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Completion Date
                  </Text>
                  <Text size="sm" fw={600}>
                    {completedDate ? dayjs(completedDate).format('MMMM D, YYYY') : '\u2014'}
                  </Text>
                </Group>
                <Divider />
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Certificate Code
                  </Text>
                  <Text size="sm" fw={600} ff="monospace">
                    {certificate.certCode}
                  </Text>
                </Group>
                <Divider />
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Expiration
                  </Text>
                  <Text size="sm" fw={600}>
                    {expiresDate ? dayjs(expiresDate).format('MMMM D, YYYY') : 'No Expiration'}
                  </Text>
                </Group>
                <Divider />
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Status
                  </Text>
                  {expiresDate ? (
                    isExpired ? (
                      <Group gap={4}>
                        <IconCircleX size={14} color="#dc2626" />
                        <Text size="sm" fw={600} c="#dc2626">
                          Expired ({dayjs(expiresDate).format('MMM D, YYYY')})
                        </Text>
                      </Group>
                    ) : (
                      <Group gap={4}>
                        <IconCircleCheck size={14} color="#16a34a" />
                        <Text size="sm" fw={600} c="#16a34a">
                          Valid (until {dayjs(expiresDate).format('MMM D, YYYY')})
                        </Text>
                      </Group>
                    )
                  ) : (
                    <Group gap={4}>
                      <IconCircleCheck size={14} color="#16a34a" />
                      <Text size="sm" fw={600} c="#16a34a">
                        Valid (No Expiration)
                      </Text>
                    </Group>
                  )}
                </Group>
              </Stack>
            </Paper>

            <Text size="xs" c="dimmed" ta="center" mt="md">
              Verified by Blackboard Training Management System
            </Text>
          </Stack>
        )}

        {/* Not Found */}
        {!loading && notFound && (
          <Stack align="center" gap="md">
            <IconCircleX size={56} color="#dc2626" />
            <Text fw={700} size="xl" c="#dc2626">
              Certificate Not Found
            </Text>
            <Text size="sm" c="dimmed" ta="center" maw={360}>
              The certificate code provided could not be verified. Please check the code and try
              again.
            </Text>
            {certCode && (
              <Paper p="sm" bg="gray.0" radius="md" withBorder>
                <Text size="xs" c="dimmed" ta="center">
                  Code entered: <Text span fw={600} ff="monospace">{certCode}</Text>
                </Text>
              </Paper>
            )}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
